import axios from 'axios';
import { Dispatch } from 'redux';
import PAY_TYPES, { PayRequestData, PayDafRequestData } from './types';
import { ENV_CONFIG } from 'config/environment';
import API_ROUTES from 'services/apiRoutes';
import { getSiteAccessToken } from 'services/storage';
import { datadogLogs } from '@datadog/browser-logs';
import { getMessageFromAxiosError } from 'helpers/errors';
import { noop } from 'helpers/utils';
import { PaymentIntent } from '@stripe/stripe-js';

const headers = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
};

export const postPayWithToken =
  (body: PayRequestData, tokenType: 'stored_token' | 'one_time_token', isLoggedIn: boolean) =>
  async (dispatch: Dispatch): Promise<any> => {
    dispatch(PAY_TYPES.payStart());

    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.PAY}/${tokenType}`;
    const payHeaders = headers;
    if (isLoggedIn) {
      payHeaders['Authorization'] = `Bearer ${getSiteAccessToken()}`;
    } else {
      payHeaders['X-API-Key'] = process.env.REACT_APP_API_KEY;
    }

    try {
      const response = await axios.post(url, body, {
        headers: payHeaders,
      });
      dispatch(PAY_TYPES.paySuccess(response.data));
      return Promise.resolve(response);
    } catch (error: any) {
      dispatch(PAY_TYPES.payError(error.message));
      return Promise.reject(error);
    }
  };

export const getPayAuthToken = async (): Promise<any> => {
  const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.PAY}/auth-token`;

  try {
    const response = await axios.get(url, {
      headers: { ...headers, Authorization: `Bearer ${getSiteAccessToken()}` },
    });
    return Promise.resolve(response);
  } catch (error: any) {
    try {
      datadogLogs.logger.warn(
        'Error occured calling GET /pay/auth-token',
        {},
        new Error(getMessageFromAxiosError(error))
      );
    } catch {
      noop();
    }
    return Promise.reject(error);
  }
};

export const postPayDaf =
  (body: PayDafRequestData, isLoggedIn: boolean) =>
  async (dispatch: Dispatch): Promise<any> => {
    dispatch(PAY_TYPES.payStart());

    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.PAY_DAF}`;
    const payHeaders = headers;
    if (isLoggedIn) {
      payHeaders['Authorization'] = `Bearer ${getSiteAccessToken()}`;
    } else {
      payHeaders['X-API-Key'] = process.env.REACT_APP_API_KEY;
    }

    try {
      const response = await axios.post(url, body, {
        headers: payHeaders,
      });
      dispatch(PAY_TYPES.payDafSuccess(response.data));
      return Promise.resolve(response);
    } catch (error: any) {
      dispatch(PAY_TYPES.payDafError(error.message));
      return Promise.reject(error);
    }
  };

export const postPaymentIntentStripeAPI = async (
  body: PayRequestData,
  canUseAuthBearerToken: boolean,
  idempotencyKey: string
) => {
  const url = body.event_ticket_purchases?.length
    ? `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.PAY_STRIPE_EVENT}`
    : `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.PAY_STRIPE}`;
  const authBearerToken = getSiteAccessToken();

  try {
    datadogLogs.logger.info(
      `Calling POST ${url} to page ${body.source_code} for logged ${
        canUseAuthBearerToken ? 'in' : 'out'
      } user with idempotency key ${idempotencyKey}.`
    );
  } catch {
    noop();
  }

  const response = await axios.post(url, body, {
    headers: {
      ...headers,
      'Idempotency-Key': idempotencyKey,
      ...(canUseAuthBearerToken
        ? { Authorization: `Bearer ${authBearerToken}` }
        : { 'X-API-Key': process.env.REACT_APP_API_KEY }),
    },
  });
  return response;
};

export const postCreateSubscriptionStripeAPI = async (
  body: PayRequestData,
  canUseAuthBearerToken: boolean,
  idempotencyKey: string
) => {
  const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.PAY_STRIPE_SUBSCRIPTION}`;
  const authBearerToken = getSiteAccessToken();

  try {
    datadogLogs.logger.info(
      `Calling POST ${url} to page ${body.source_code} for logged ${
        canUseAuthBearerToken ? 'in' : 'out'
      } user with idempotency key ${idempotencyKey}.`
    );
  } catch {
    noop();
  }

  const response = await axios.post(url, body, {
    headers: {
      ...headers,
      'Idempotency-Key': idempotencyKey,
      ...(canUseAuthBearerToken
        ? { Authorization: `Bearer ${authBearerToken}` }
        : { 'X-API-Key': process.env.REACT_APP_API_KEY }),
    },
  });
  return response;
};

export const storeStripePaymentIntent =
  (paymentIntent: PaymentIntent) =>
  async (dispatch: Dispatch): Promise<any> => {
    dispatch(PAY_TYPES.confirmStripePaymentIntentSuccess(paymentIntent));
  };

export const postTransactionWire =
  (body: PayRequestData, idempotencyKey: string, isLoggedIn: boolean) =>
  async (dispatch: Dispatch): Promise<any> => {
    dispatch(PAY_TYPES.payStart());

    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.TRANSACTION_WIRE}`;
    const payHeaders = headers;
    payHeaders['Idempotency-Key'] = idempotencyKey;
    if (isLoggedIn) {
      payHeaders['Authorization'] = `Bearer ${getSiteAccessToken()}`;
    } else {
      payHeaders['X-API-Key'] = process.env.REACT_APP_API_KEY;
    }

    try {
      const response = await axios.post(url, body, {
        headers: payHeaders,
      });
      dispatch(PAY_TYPES.transactionsSuccess(response.data));
      return Promise.resolve(response);
    } catch (error: any) {
      dispatch(PAY_TYPES.payError(error.message));
      return Promise.reject(error);
    }
  };
